import React, { useState } from 'react';
import {
  Container,
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Fade,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import SwalWithA11yFix from '../../services/swalConfig';
import api from '../../services/api';
import { Visibility, VisibilityOff } from '@mui/icons-material';

const AdminLogin = () => {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    // Prevent default form submission behavior - MUST be first
    if (e) {
      if (e.preventDefault) {
        e.preventDefault();
      }
      if (e.stopPropagation) {
        e.stopPropagation();
      }
      if (e.stopImmediatePropagation) {
        e.stopImmediatePropagation();
      }
      if (e.persist) {
        e.persist(); // For React synthetic events
      }
      
      // Prevent any default browser behavior
      if (e.defaultPrevented === false) {
        e.preventDefault();
      }
    }
    
    // Client-side validation
    if (!formData.email || !formData.password) {
      try {
        await SwalWithA11yFix({
          icon: 'warning',
          title: 'Missing Information',
          text: 'Please enter both email and password',
          confirmButtonColor: '#1976d2',
        });
      } catch (swalError) {
        console.error('SweetAlert error:', swalError);
      }
      return;
    }

    setLoading(true);

    try {
      const response = await api.post('/auth/admin/login', formData);
      
      // Check if response is valid
      if (!response || !response.data || !response.data.user) {
        throw new Error('Invalid response from server');
      }
      
      // Only proceed if we have valid token and user
      if (!response.data.token) {
        throw new Error('No authentication token received');
      }
      
      login(response.data.token, response.data.user);
      
      // Show success message
      await SwalWithA11yFix({
        icon: 'success',
        title: 'Welcome Admin!',
        text: 'Successfully signed in as Administrator',
        timer: 2000,
        showConfirmButton: false,
      });
      
      // Only navigate on success
      navigate('/admin/dashboard');
    } catch (err) {
      // CRITICAL: Set loading to false FIRST - before anything else
      setLoading(false);
      
      // Extract error message BEFORE showing alert
      let errorMsg = 'Login failed. Please check your credentials and try again.';
      
      if (err.response) {
        // Server responded with error
        errorMsg = err.response.data?.message || 
                   (Array.isArray(err.response.data?.errors) 
                     ? err.response.data.errors.join(', ') 
                     : 'Invalid credentials. Please try again.');
      } else if (err.message) {
        // Network or other error
        errorMsg = err.message;
      }
      
      // Show error using SweetAlert - MUST await to block execution
      // Use setTimeout to ensure it runs after any pending navigation
      setTimeout(async () => {
        try {
          await SwalWithA11yFix({
            icon: 'error',
            title: 'Login Failed',
            html: `<div style="text-align: center; padding: 10px;">${errorMsg}</div>`,
            confirmButtonColor: '#1976d2',
            confirmButtonText: 'OK',
            allowOutsideClick: false,
            allowEscapeKey: true,
            allowEnterKey: false,
            showCloseButton: true,
            focusConfirm: true,
            backdrop: true,
            backdropClass: 'swal2-backdrop-show',
            didOpen: () => {
              // Prevent any form interactions while alert is open
              const forms = document.querySelectorAll('form');
              forms.forEach(form => {
                form.style.pointerEvents = 'none';
                form.addEventListener('submit', (e) => e.preventDefault(), true);
              });
            },
            didClose: () => {
              // Re-enable form interactions after alert closes
              const forms = document.querySelectorAll('form');
              forms.forEach(form => {
                form.style.pointerEvents = 'auto';
              });
            },
          });
        } catch (swalError) {
          console.error('SweetAlert error:', swalError);
          // Fallback: show browser alert if SweetAlert fails
          alert(errorMsg);
        }
      }, 0);
      
      // CRITICAL: Return immediately - don't continue execution
      return false;
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        padding: 2,
      }}
    >
      <Fade in={true} timeout={500}>
        <Container maxWidth="sm">
          <Paper
            elevation={3}
            sx={{
              padding: 4,
              borderRadius: 3,
              backgroundColor: 'white',
            }}
          >
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography variant="h4" component="h1" gutterBottom fontWeight="bold" sx={{ color: '#1976d2' }}>
                AnalytikalDNA
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                Admin Login
              </Typography>
            </Box>


            <Box
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  e.stopPropagation();
                  e.stopImmediatePropagation?.();
                  // Prevent any form submission
                  const target = e.target;
                  if (target && target.form) {
                    e.preventDefault();
                  }
                  handleSubmit(e);
                }
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  e.stopPropagation();
                }
              }}
            >
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                  Email Address <span style={{ color: 'red' }}>*</span>
                </Typography>
                <TextField
                  fullWidth
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      e.stopPropagation();
                      e.stopImmediatePropagation?.();
                      // Prevent any form submission
                      const target = e.target;
                      if (target && target.closest('form')) {
                        e.preventDefault();
                      }
                      handleSubmit(e);
                    }
                  }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      e.stopPropagation();
                    }
                  }}
                  placeholder="Enter your email"
                  autoComplete="email"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#f5f5f5',
                      '&:hover fieldset': {
                        borderColor: '#1976d2',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#1976d2',
                      },
                    },
                  }}
                />
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                  Password <span style={{ color: 'red' }}>*</span>
                </Typography>
                <TextField
                  fullWidth
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      e.stopPropagation();
                      e.stopImmediatePropagation?.();
                      // Prevent any form submission
                      const target = e.target;
                      if (target && target.closest('form')) {
                        e.preventDefault();
                      }
                      handleSubmit(e);
                    }
                  }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      e.stopPropagation();
                    }
                  }}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#f5f5f5',
                      '&:hover fieldset': {
                        borderColor: '#1976d2',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#1976d2',
                      },
                    },
                  }}
                />
              </Box>

              <Button
                type="button"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                onClick={async (e) => {
                  // Prevent all default behavior
                  e.preventDefault();
                  e.stopPropagation();
                  if (e.stopImmediatePropagation) {
                    e.stopImmediatePropagation();
                  }
                  if (e.persist) {
                    e.persist();
                  }
                  
                  // Prevent form submission if button is in a form
                  const button = e.currentTarget;
                  if (button && button.form) {
                    button.form.preventDefault?.();
                  }
                  
                  // Call handleSubmit
                  await handleSubmit(e);
                }}
                sx={{
                  py: 1.5,
                  mb: 2,
                  backgroundColor: '#1976d2',
                  textTransform: 'none',
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  borderRadius: 2,
                  '&:hover': {
                    backgroundColor: '#1565c0',
                  },
                }}
              >
                {loading ? 'Logging in...' : 'Sign in as Admin'}
              </Button>

              {/* Back to Regular Login */}
              <Button
                fullWidth
                variant="text"
                size="small"
                sx={{
                  mt: 1,
                  color: '#666',
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  fontWeight: 400,
                  '&:hover': {
                    backgroundColor: 'rgba(102, 102, 102, 0.04)',
                    color: '#333',
                  },
                }}
                onClick={() => navigate('/login')}
              >
                ← Back to Regular Login
              </Button>
            </Box>
          </Paper>
        </Container>
      </Fade>
    </Box>
  );
};

export default AdminLogin;
